'use client';

import React, { useState } from 'react';
import { Search, MapPin, SlidersHorizontal, RotateCcw } from 'lucide-react';

export default function DiscoveryFilters() {
  const [location, setLocation] = useState('Boulder, CO');
  const [radius, setRadius] = useState(15);
  const [minDist, setMinDist] = useState(0.5);
  const [maxDist, setMaxDist] = useState(10);
  const [minElev, setMinElev] = useState(0);
  const [maxElev, setMaxElev] = useState(500);
  const [minGrade, setMinGrade] = useState(0);
  const [maxGrade, setMaxGrade] = useState(20);
  const [minProb, setMinProb] = useState(0);
  const [segmentType, setSegmentType] = useState<'all' | 'climb' | 'flat' | 'descent'>('all');

  const handleReset = () => {
    setLocation('Boulder, CO');
    setRadius(15);
    setMinDist(0.5);
    setMaxDist(10);
    setMinElev(0);
    setMaxElev(500);
    setMinGrade(0);
    setMaxGrade(20);
    setMinProb(0);
    setSegmentType('all');
  };

  return (
    <aside className="w-64 shrink-0 border-r border-[hsl(var(--border))] bg-white flex flex-col overflow-y-auto scrollbar-thin">
      <div className="p-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={14} className="text-[hsl(var(--muted-foreground))]" />
          <span className="text-sm font-semibold text-[hsl(var(--foreground))]">Filters</span>
        </div>
        <button
          onClick={handleReset}
          className="flex items-center gap-1 text-[10px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
        >
          <RotateCcw size={10} />
          Reset
        </button>
      </div>

      <div className="p-4 space-y-6">
        {/* Location */}
        <div>
          <label className="block text-xs font-semibold text-[hsl(var(--foreground))] mb-2 tracking-wide">
            Location
          </label>
          <div className="relative">
            <MapPin size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full pl-7 pr-3 py-2 text-xs rounded-lg border border-[hsl(var(--border))] bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--primary))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]"
              placeholder="City, region or address"
            />
          </div>
          <button className="mt-1.5 flex items-center gap-1 text-[10px] text-[hsl(var(--primary))] font-medium hover:underline">
            <Search size={9} />
            Use my current location
          </button>
        </div>

        {/* Radius */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-[hsl(var(--foreground))] tracking-wide">
              Search Radius
            </label>
            <span className="text-xs font-bold font-mono text-[hsl(var(--primary))] tabular-nums">{radius} km</span>
          </div>
          <input
            type="range"
            min={1}
            max={50}
            value={radius}
            onChange={(e) => setRadius(Number(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none bg-[hsl(var(--border))] cursor-pointer accent-[#FC4C02]"
          />
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">1 km</span>
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">50 km</span>
          </div>
        </div>

        {/* Segment type */}
        <div>
          <label className="block text-xs font-semibold text-[hsl(var(--foreground))] mb-2 tracking-wide">
            Segment Type
          </label>
          <div className="grid grid-cols-2 gap-1.5">
            {(['all', 'climb', 'flat', 'descent'] as const).map((type) => (
              <button
                key={`type-${type}`}
                onClick={() => setSegmentType(type)}
                className={`py-1.5 px-2 rounded-lg text-[11px] font-medium capitalize transition-all ${
                  segmentType === type
                    ? 'bg-[hsl(var(--primary))] text-white'
                    : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--border))]'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Min win probability */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-[hsl(var(--foreground))] tracking-wide">
              Min Win Probability
            </label>
            <span className="text-xs font-bold font-mono text-[hsl(var(--primary))] tabular-nums">{minProb}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={95}
            step={5}
            value={minProb}
            onChange={(e) => setMinProb(Number(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none bg-[hsl(var(--border))] cursor-pointer accent-[#FC4C02]"
          />
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Any</span>
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">95%+</span>
          </div>
        </div>

        {/* Distance range */}
        <div>
          <label className="block text-xs font-semibold text-[hsl(var(--foreground))] mb-2 tracking-wide">
            Segment Distance
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <input
                type="number"
                min={0.1}
                max={maxDist}
                step={0.1}
                value={minDist}
                onChange={(e) => setMinDist(Number(e.target.value))}
                className="w-full px-2 py-1.5 text-xs rounded-lg border border-[hsl(var(--border))] bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--primary))] font-mono tabular-nums text-center"
              />
              <p className="text-[10px] text-center text-[hsl(var(--muted-foreground))] mt-0.5">Min km</p>
            </div>
            <span className="text-[hsl(var(--muted-foreground))] text-xs">—</span>
            <div className="flex-1">
              <input
                type="number"
                min={minDist}
                max={50}
                step={0.5}
                value={maxDist}
                onChange={(e) => setMaxDist(Number(e.target.value))}
                className="w-full px-2 py-1.5 text-xs rounded-lg border border-[hsl(var(--border))] bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--primary))] font-mono tabular-nums text-center"
              />
              <p className="text-[10px] text-center text-[hsl(var(--muted-foreground))] mt-0.5">Max km</p>
            </div>
          </div>
        </div>

        {/* Elevation gain */}
        <div>
          <label className="block text-xs font-semibold text-[hsl(var(--foreground))] mb-2 tracking-wide">
            Elevation Gain (m)
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <input
                type="number"
                min={0}
                max={maxElev}
                step={10}
                value={minElev}
                onChange={(e) => setMinElev(Number(e.target.value))}
                className="w-full px-2 py-1.5 text-xs rounded-lg border border-[hsl(var(--border))] bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--primary))] font-mono tabular-nums text-center"
              />
              <p className="text-[10px] text-center text-[hsl(var(--muted-foreground))] mt-0.5">Min m</p>
            </div>
            <span className="text-[hsl(var(--muted-foreground))] text-xs">—</span>
            <div className="flex-1">
              <input
                type="number"
                min={minElev}
                max={2000}
                step={25}
                value={maxElev}
                onChange={(e) => setMaxElev(Number(e.target.value))}
                className="w-full px-2 py-1.5 text-xs rounded-lg border border-[hsl(var(--border))] bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--primary))] font-mono tabular-nums text-center"
              />
              <p className="text-[10px] text-center text-[hsl(var(--muted-foreground))] mt-0.5">Max m</p>
            </div>
          </div>
        </div>

        {/* Grade */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-[hsl(var(--foreground))] tracking-wide">
              Avg Grade
            </label>
            <span className="text-xs font-bold font-mono text-[hsl(var(--primary))] tabular-nums">{minGrade}–{maxGrade}%</span>
          </div>
          <div className="space-y-2">
            <input
              type="range"
              min={-10}
              max={maxGrade}
              value={minGrade}
              onChange={(e) => setMinGrade(Number(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none bg-[hsl(var(--border))] cursor-pointer accent-[#FC4C02]"
            />
            <input
              type="range"
              min={minGrade}
              max={30}
              value={maxGrade}
              onChange={(e) => setMaxGrade(Number(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none bg-[hsl(var(--border))] cursor-pointer accent-[#FC4C02]"
            />
          </div>
        </div>

        {/* Apply button */}
        <button className="w-full py-2.5 rounded-xl bg-[hsl(var(--primary))] text-white text-xs font-semibold hover:bg-[#e84400] transition-colors active:scale-95">
          Apply Filters
        </button>
      </div>
    </aside>
  );
}